/*
 * The one owner of workflow-plan knowledge paths, across both phases of a plan's life.
 *
 * A plan is authored during requirements mode before any workflow exists, so it is staged under
 * `threads/<threadId>/requirements/` and moved onto `workflows/<workflowId>/requirements/` inside the
 * workflow-creation transaction. The API writes and moves those paths, the renderer resolves which one
 * to read and whether a streamed knowledge_write touched it, and the write guard rejects paths
 * addressed to a workflow that does not exist — so all of them need the same strings.
 *
 * It lives in shared because it was previously re-implemented in seven places across api and pieve,
 * and those copies had already drifted: the renderer built the workflow path without slugifying it, so
 * any workflow id outside `[a-z0-9_-]` would have produced a path the API never wrote.
 */
import { toSafeString } from '../utils/toSafeString.js';

export const WORKFLOW_PLAN_WORKFLOW_ROOT = 'workflows';
// Pre-draft plans are staged under their own root. It is deliberately absent from
// listPieveKnowledgeTree's TYPE_PREFIXES: a plan with no workflow is thread-local working state, and
// most planning threads are abandoned, so listing it would put a uuid-named folder per abandoned
// attempt into the user's visible knowledge tree.
export const WORKFLOW_PLAN_THREAD_ROOT = 'threads';
export const WORKFLOW_PLAN_DIR_NAME = 'requirements';
export const WORKFLOW_PLAN_FILE_NAME = 'workflow-requirements.md';
// Sits beside the plan in the same requirements directory, so the creation rehome carries it across
// with everything else without needing its own move.
export const WORKFLOW_PLANNED_APPS_FILE_NAME = 'planned-apps.json';
// The machine-readable half of the plan: which scope decisions the user actually confirmed, which
// planned actions were probed against live data, and what a passing test must show. The prose plan is
// for the user; this is what the build gate can check. Same directory, so the rehome carries it too.
export const WORKFLOW_PLAN_MANIFEST_FILE_NAME = 'plan-manifest.json';

export const toWorkflowPlanPathSlug = (value) => {
  return toSafeString(value)
    .toLowerCase()
    .replace(/[^a-z0-9_-]/gu, '-')
    .replace(/-{2,}/gu, '-')
    .replace(/^-+/gu, '')
    .replace(/-+$/gu, '');
};

const buildPlanPaths = ({ root, id }) => {
  const slug = toWorkflowPlanPathSlug(id);

  if (!slug) {
    return null;
  }

  const rootDirPath = `${root}/${slug}/${WORKFLOW_PLAN_DIR_NAME}`;

  return {
    slug,
    rootDirPath,
    rootPath: `${rootDirPath}/${WORKFLOW_PLAN_FILE_NAME}`,
    plannedAppsPath: `${rootDirPath}/${WORKFLOW_PLANNED_APPS_FILE_NAME}`,
    manifestPath: `${rootDirPath}/${WORKFLOW_PLAN_MANIFEST_FILE_NAME}`
  };
};

export const buildWorkflowRequirementsPaths = ({ workflowId } = {}) => {
  return buildPlanPaths({
    root: WORKFLOW_PLAN_WORKFLOW_ROOT,
    id: workflowId
  });
};

export const buildThreadRequirementsPaths = ({ threadId } = {}) => {
  return buildPlanPaths({
    root: WORKFLOW_PLAN_THREAD_ROOT,
    id: threadId
  });
};

/*
 * The single rule for where a workflow plan document lives.
 *
 * A draft workflow exists -> the canonical workflow-keyed path. No draft -> the thread-keyed staging
 * path. This is one rule keyed on draft existence, not a tolerant fallback: once a workflowId exists
 * only the workflow path is consulted and a stray thread-keyed document is never read, so exactly one
 * key is populated per regime and a missing document means missing.
 */
export const resolveWorkflowPlanKnowledgePath = ({ workflowId = null, threadId = null } = {}) => {
  return buildWorkflowRequirementsPaths({ workflowId })?.rootPath || buildThreadRequirementsPaths({ threadId })?.rootPath || null;
};

const PLAN_DIR_PATTERN = new RegExp(`^(?:${WORKFLOW_PLAN_WORKFLOW_ROOT}|${WORKFLOW_PLAN_THREAD_ROOT})/[^/]+/${WORKFLOW_PLAN_DIR_NAME}/`, 'u');

// Matches both homes a plan doc has across its life, so a caller can tell a plan write from any other
// knowledge write without knowing which phase the thread is in.
export const isWorkflowPlanKnowledgePath = (path) => {
  return PLAN_DIR_PATTERN.test(toSafeString(path));
};

// The manifest is written only through workflow_plan_manifest_update, whose schema validates it. A raw
// knowledge_write of hand-rolled JSON is refused so a malformed manifest cannot exist to be read.
export const isWorkflowPlanManifestKnowledgePath = (path) => {
  const safePath = toSafeString(path);

  return PLAN_DIR_PATTERN.test(safePath) && safePath.endsWith(`/${WORKFLOW_PLAN_MANIFEST_FILE_NAME}`);
};
